/**
 * Historial Diario de Inventario.
 *
 * Vista por día:
 *  - Snapshot compacto (Total + cada tipo al cierre del día).
 *  - Al hacer clic en una fila se despliega el detalle de flujo:
 *      Inicial → +Entradas −Salidas ±Ajustes = Final  (por cada tipo)
 *  - Días con diferencia entre Stock Final calculado y Conteo Físico se
 *    resaltan en rojo para facilitar la auditoría.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, History, RefreshCw, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { getInventarioHistorialDiario } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { EGG_LABELS } from './constants';
import { formatDate } from '../../lib/dateUtils';

const TIPOS = ['grande', 'mediano', 'pequeño', 'doble', 'sucio', 'casquiado'];

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('es-ES') : '0');

const initialRange = () => {
  // Por defecto: últimos 14 días
  const hoy = new Date();
  const desde = new Date(hoy);
  desde.setDate(desde.getDate() - 13);
  const iso = (d) => d.toISOString().split('T')[0];
  return { desde: iso(desde), hasta: iso(hoy) };
};

export const HistorialDiarioTab = () => {
  const { isConteoCiego } = useAuth();
  // En Modo Conteo Ciego se ocultan las cifras del sistema: Stock Inicial,
  // Entradas, Salidas, Ajustes, Stock Final, Diferencia. Solo se muestra la
  // información básica del día (fecha) y el físico que el propio usuario
  // capturó — sin poder compararlo contra el sistema.
  const canVerSistema = !isConteoCiego;
  const [{ desde, hasta }, setRango] = useState(initialRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // { fecha: true }

  const load = useCallback(async (params) => {
    try {
      setLoading(true);
      const res = await getInventarioHistorialDiario(params);
      setData(res.data);
    } catch (err) {
      console.error('Error historial:', err);
      toast.error('No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carga inicial con rango por defecto
  useEffect(() => {
    load({ fecha_desde: desde, fecha_hasta: hasta });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAplicar = () => {
    if (!desde || !hasta) {
      toast.error('Selecciona ambas fechas');
      return;
    }
    if (desde > hasta) {
      toast.error('La fecha inicial no puede ser mayor a la final');
      return;
    }
    load({ fecha_desde: desde, fecha_hasta: hasta });
  };

  const toggleRow = (fecha) => {
    setExpanded((prev) => ({ ...prev, [fecha]: !prev[fecha] }));
  };

  const dias = data?.dias || [];

  // Resumen del período mostrado
  const resumen = useMemo(() => {
    if (!dias.length) return null;
    const totalEntradas = dias.reduce((s, d) => s + (d.total_entradas || 0), 0);
    const totalSalidas = dias.reduce((s, d) => s + (d.total_salidas || 0), 0);
    const diasConDiferencia = dias.filter((d) => d.tiene_diferencia).length;
    const ultimo = dias[0]; // están en orden desc
    const primero = dias[dias.length - 1];
    return {
      stockInicialPeriodo: primero?.total_inicial ?? 0,
      stockFinalPeriodo: ultimo?.total_final ?? 0,
      totalEntradas,
      totalSalidas,
      diasConDiferencia,
      diasTotales: dias.length,
    };
  }, [dias]);

  return (
    <Card data-testid="historial-diario-card">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Historial Diario de Inventario
          </CardTitle>
          {resumen && canVerSistema && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline" className="font-normal">
                {resumen.diasTotales} día(s)
              </Badge>
              <Badge variant="outline" className="font-normal text-green-700 border-green-300 bg-green-50">
                +{fmt(resumen.totalEntradas)} entradas
              </Badge>
              <Badge variant="outline" className="font-normal text-red-700 border-red-300 bg-red-50">
                −{fmt(resumen.totalSalidas)} salidas
              </Badge>
              {resumen.diasConDiferencia > 0 && (
                <Badge variant="destructive" className="font-normal gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {resumen.diasConDiferencia} día(s) con diferencia
                </Badge>
              )}
            </div>
          )}
          {resumen && !canVerSistema && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline" className="font-normal">
                {resumen.diasTotales} día(s)
              </Badge>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {/* Filtros */}
        <div className="flex flex-wrap items-end gap-3 mb-4 pb-4 border-b">
          <div className="flex flex-col gap-1">
            <Label htmlFor="hist-desde" className="text-xs text-muted-foreground">Desde</Label>
            <Input
              id="hist-desde"
              type="date"
              value={desde}
              onChange={(e) => setRango((p) => ({ ...p, desde: e.target.value }))}
              className="w-[160px] h-9"
              data-testid="historial-fecha-desde"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="hist-hasta" className="text-xs text-muted-foreground">Hasta</Label>
            <Input
              id="hist-hasta"
              type="date"
              value={hasta}
              onChange={(e) => setRango((p) => ({ ...p, hasta: e.target.value }))}
              className="w-[160px] h-9"
              data-testid="historial-fecha-hasta"
            />
          </div>
          <Button onClick={handleAplicar} disabled={loading} className="h-9" data-testid="historial-aplicar">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Cargando…' : 'Aplicar'}
          </Button>
          <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
            {canVerSistema && (
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-red-100 border border-red-300" /> Día con diferencia vs. conteo físico
              </span>
            )}
            <span className={`inline-flex items-center gap-1 ${canVerSistema ? 'ml-3' : ''}`}>
              <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> Día con conteo físico
            </span>
          </div>
        </div>

        {/* Tabla */}
        {!data || dias.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{loading ? 'Cargando historial…' : 'Sin datos en el rango seleccionado'}</p>
          </div>
        ) : (
          <Table
            wrapperClassName="max-h-[calc(100vh-360px)] rounded-md border border-border/40"
            data-testid="tabla-historial-diario"
          >
            <TableHeader className="sticky top-0 z-30 bg-card shadow-[0_2px_4px_-1px_rgba(0,0,0,0.08)] [&_tr]:border-b-0 [&_th]:border-b [&_th]:border-border">
              <TableRow>
                {canVerSistema && <TableHead className="w-8"></TableHead>}
                <TableHead>Fecha</TableHead>
                {canVerSistema && TIPOS.map((t) => (
                  <TableHead key={t} className="text-right">{EGG_LABELS[t === 'pequeño' ? 'pequeno' : t]}</TableHead>
                ))}
                {canVerSistema && <TableHead className="text-right font-semibold">Total</TableHead>}
                <TableHead className="text-center w-24">Conteo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dias.map((d) => {
                const open = !!expanded[d.fecha];
                const rowHighlight = (d.tiene_diferencia && canVerSistema)
                  ? 'bg-red-50/70 hover:bg-red-100/60'
                  : 'hover:bg-accent/50';
                return (
                  <React.Fragment key={d.fecha}>
                    <TableRow
                      className={`transition-colors ${canVerSistema ? 'cursor-pointer' : ''} ${rowHighlight}`}
                      onClick={() => canVerSistema && toggleRow(d.fecha)}
                      data-testid={`historial-fila-${d.fecha}`}
                    >
                      {canVerSistema && (
                        <TableCell className="text-muted-foreground">
                          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </TableCell>
                      )}
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {formatDate(d.fecha)}
                          {d.tiene_diferencia && canVerSistema && (
                            <Badge variant="destructive" className="h-5 px-1.5 gap-1 text-[10px]">
                              <AlertTriangle className="w-3 h-3" />
                              Diferencia
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      {canVerSistema && TIPOS.map((t) => {
                        const v = d.por_tipo?.[t];
                        const dif = v?.diferencia;
                        return (
                          <TableCell
                            key={t}
                            className={`text-right tabular-nums ${dif && dif !== 0 ? 'text-red-700 font-semibold' : ''}`}
                            title={
                              v
                                ? `Inicial: ${fmt(v.inicial)} | +${fmt(v.entradas)} | −${fmt(v.salidas)} | Ajustes: ${fmt(v.ajustes)}${v.fisico != null ? ` | Físico: ${fmt(v.fisico)}` : ''}`
                                : ''
                            }
                          >
                            {fmt(v?.final ?? 0)}
                          </TableCell>
                        );
                      })}
                      {canVerSistema && (
                        <TableCell className="text-right font-bold tabular-nums">
                          {fmt(d.total_final)}
                        </TableCell>
                      )}
                      <TableCell className="text-center">
                        {d.tiene_conteo ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 inline" />
                        ) : (
                          <span className="text-muted-foreground/50">—</span>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Detalle expandido — solo si el usuario puede ver el sistema */}
                    {open && canVerSistema && (
                      <TableRow className="bg-muted/30 hover:bg-muted/40" data-testid={`historial-detalle-${d.fecha}`}>
                        <TableCell colSpan={TIPOS.length + 4} className="p-0">
                          <div className="px-4 py-3">
                            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                              Flujo del día — {formatDate(d.fecha)}
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-muted-foreground border-b">
                                    <th className="text-left py-1.5 pr-3 font-medium">Tipo</th>
                                    <th className="text-right py-1.5 px-2 font-medium">Stock Inicial</th>
                                    <th className="text-right py-1.5 px-2 font-medium text-green-700">+ Entradas</th>
                                    <th className="text-right py-1.5 px-2 font-medium text-red-700">− Salidas</th>
                                    <th className="text-right py-1.5 px-2 font-medium text-amber-700">± Ajustes</th>
                                    <th className="text-right py-1.5 px-2 font-medium">= Stock Final</th>
                                    <th className="text-right py-1.5 px-2 font-medium">Físico</th>
                                    <th className="text-right py-1.5 pl-2 font-medium">Diferencia</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {TIPOS.map((t) => {
                                    const v = d.por_tipo?.[t] || {};
                                    const dif = v.diferencia;
                                    const tieneDif = dif != null && dif !== 0;
                                    return (
                                      <tr
                                        key={t}
                                        className={`border-b border-border/30 last:border-b-0 ${tieneDif ? 'bg-red-50/50' : ''}`}
                                      >
                                        <td className="py-1.5 pr-3 font-medium">{EGG_LABELS[t === 'pequeño' ? 'pequeno' : t]}</td>
                                        <td className="text-right py-1.5 px-2 tabular-nums">{fmt(v.inicial)}</td>
                                        <td className="text-right py-1.5 px-2 tabular-nums text-green-700">
                                          {v.entradas > 0 ? `+${fmt(v.entradas)}` : '—'}
                                        </td>
                                        <td className="text-right py-1.5 px-2 tabular-nums text-red-700">
                                          {v.salidas > 0 ? `−${fmt(v.salidas)}` : '—'}
                                        </td>
                                        <td className="text-right py-1.5 px-2 tabular-nums text-amber-700">
                                          {v.ajustes !== 0 ? (v.ajustes > 0 ? `+${fmt(v.ajustes)}` : fmt(v.ajustes)) : '—'}
                                        </td>
                                        <td className="text-right py-1.5 px-2 tabular-nums font-semibold">{fmt(v.final)}</td>
                                        <td className="text-right py-1.5 px-2 tabular-nums">
                                          {v.fisico != null ? fmt(v.fisico) : <span className="text-muted-foreground/50">—</span>}
                                        </td>
                                        <td
                                          className={`text-right py-1.5 pl-2 tabular-nums font-semibold ${
                                            tieneDif ? 'text-red-700' : v.fisico != null ? 'text-green-700' : 'text-muted-foreground/50'
                                          }`}
                                        >
                                          {v.fisico != null
                                            ? dif > 0
                                              ? `+${fmt(dif)}`
                                              : dif < 0
                                                ? fmt(dif)
                                                : '0'
                                            : '—'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {/* Fila de totales */}
                                  <tr className="font-bold border-t-2 border-border">
                                    <td className="py-1.5 pr-3">Total</td>
                                    <td className="text-right py-1.5 px-2 tabular-nums">{fmt(d.total_inicial)}</td>
                                    <td className="text-right py-1.5 px-2 tabular-nums text-green-700">+{fmt(d.total_entradas)}</td>
                                    <td className="text-right py-1.5 px-2 tabular-nums text-red-700">−{fmt(d.total_salidas)}</td>
                                    <td className="text-right py-1.5 px-2 tabular-nums text-amber-700">
                                      {d.total_ajustes > 0 ? `+${fmt(d.total_ajustes)}` : fmt(d.total_ajustes)}
                                    </td>
                                    <td className="text-right py-1.5 px-2 tabular-nums">{fmt(d.total_final)}</td>
                                    <td className="text-right py-1.5 px-2 tabular-nums text-muted-foreground"></td>
                                    <td className="text-right py-1.5 pl-2 tabular-nums text-muted-foreground"></td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default HistorialDiarioTab;
